/**
 * extensions/dicom-rt/src/components/LongitudinalStudy/sections/FindingSection.js
 *
 * v4 BATCH C:
 *   - Under RECIP10, use getRecipTimepointStats with the UNION-filtered ROI
 *     set (no PSMA pre-filter) and pass filteredPsmaStatus separately so the
 *     stats function does per-tp Writing-B PSMA gating itself.
 *   - This is what makes Finding sums match Response PSMA-VOL.
 *   - Non-RECIP guidelines keep the existing getTimepointStats path.
 *
 *   - 5-filter row under RECIP (unchanged from prior patch).
 */

import React from 'react';
import PropTypes from 'prop-types';

import {
  Body,
  Container,
  Grid,
  Select,
  H6,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@platform/ui';
import module from '../../../modules/dicom-measurement/src';
import getRecipTimepointStats from '../../../modules/dicom-measurement/src/assessments/getRecipTimepointStats';

const { guideline, getTimepoints, getTimepointStats } = module;
const {
  MEASUREMENT_TECHNIQUE,
  getMeasurementTechniques,
  getBodyParts,
  getTrackingTypes,
  getPsmaStatusFilters,
} = guideline;

function addSign(num) {
  if (!isFinite(num)) return '—';
  return num > 0 ? `+${num}` : num;
}

function getUnit(measurementTechnique) {
  const mapping = {
    [MEASUREMENT_TECHNIQUE.BIDIMENSIONAL]: <span>Sum(mm)</span>,
    [MEASUREMENT_TECHNIQUE.VOLUMETRIC]: (
      <span>
        Sum(mm<sup>3</sup>)
      </span>
    ),
  };
  return mapping[measurementTechnique];
}

export const FindingSection = ({
  imageSets,
  ROIContours,
  criteria,
  assessments,
  getFilteredROIContours,
  getRoiUnionExcludingPsma,
  filteredBodyPart,
  setFilteredBodyPart,
  filteredTrackingType,
  setFilteredTrackingType,
  filteredPsmaStatus,
  setFilteredPsmaStatus,
  selectedLesionNumber,
  setSelectedLesionNumber,
  measurementTechnique,
  setMeasurementTechnique,
}) => {
  const isRecip = criteria?.guidelineId === 'RECIP10';

  /* Build the ROI cohort + per-tp stats with the correct semantics for
   * each guideline. */
  let timepoints, timepointStats;
  if (isRecip) {
    /* RECIP: use UNION (no PSMA pre-filter) + per-tp gating via
     * getRecipTimepointStats. */
    const union = getRoiUnionExcludingPsma
      ? getRoiUnionExcludingPsma(ROIContours)
      : getFilteredROIContours(ROIContours);
    timepoints = getTimepoints(imageSets, union);
    timepointStats = getRecipTimepointStats(
      timepoints,
      union,
      [],
      criteria,
      filteredPsmaStatus || 'all'
    );
  } else {
    /* Non-RECIP: legacy behaviour. */
    const filteredROIContours = getFilteredROIContours(ROIContours);
    timepoints = getTimepoints(imageSets, filteredROIContours);
    timepointStats = getTimepointStats(
      timepoints,
      filteredROIContours,
      [],
      criteria,
      assessments
    );
  }

  const reports = timepoints.map((timepoint, index) => {
    const techniqueKeyMapping = {
      [MEASUREMENT_TECHNIQUE.BIDIMENSIONAL]: 'sumDiameter',
      [MEASUREMENT_TECHNIQUE.VOLUMETRIC]: 'sumVolume',
    };
    const key = techniqueKeyMapping[measurementTechnique];
    const sum = timepointStats[index]?.summation?.[key];
    const fromBaseline = timepointStats[index]?.fromBaseline?.[key];
    const fromNadir = timepointStats[index]?.fromNadir?.[key];
    const fromLatest = timepointStats[index]?.fromLatest?.[key];
    return { timepoint, sum, fromBaseline, fromNadir, fromLatest };
  });

  const onBodyPartChange = evt => setFilteredBodyPart(evt.target.value);
  const onTrackingTypeChange = evt => {
    const type = evt.target.value;
    setFilteredTrackingType(type);
    if (type !== 'selected') setSelectedLesionNumber(-1);
  };
  const onPsmaStatusChange = evt => setFilteredPsmaStatus?.(evt.target.value);
  const onLesionNumberChange = evt =>
    setSelectedLesionNumber(Number(evt.target.value));
  const onTechniqueChange = evt => setMeasurementTechnique(evt.target.value);

  return (
    <Body>
      <Container>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow index={-1}>
              {[
                ['No.', '6%'],
                ['Date', '14%'],
                [getUnit(measurementTechnique), '20%'],
                ['from Baseline(%)', '20%'],
                ['from Nadir(%)', '20%'],
                ['from Latest(%)', '20%'],
              ].map(([text, width], i) => (
                <TableCell key={i} index={i} width={width} header={1}>
                  <H6>{text}</H6>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.map((report, index) => {
              return (
                <TableRow
                  key={report.timepoint.SeriesDate + index}
                  index={index}
                >
                  {[
                    index,
                    report.timepoint.SeriesDate,
                    report.sum,
                    addSign(report.fromBaseline),
                    addSign(report.fromNadir),
                    addSign(report.fromLatest),
                  ].map((text, i) => (
                    <TableCell key={i} index={i}>
                      <H6>{text}</H6>
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Container>
      <Grid container spacing={2}>
        <Grid item xs={isRecip ? 2 : 3}>
          <Select value={filteredBodyPart} onChange={onBodyPartChange}>
            {getBodyParts().map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </Select>
        </Grid>
        <Grid item xs={isRecip ? 2 : 3}>
          <Select value={filteredTrackingType} onChange={onTrackingTypeChange}>
            {getTrackingTypes(criteria.guidelineId).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </Select>
        </Grid>
        {isRecip && (
          <Grid item xs={2}>
            <Select
              value={filteredPsmaStatus || 'all'}
              onChange={onPsmaStatusChange}
            >
              {getPsmaStatusFilters().map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Select>
          </Grid>
        )}
        <Grid item xs={isRecip ? 3 : 3}>
          <Select value={selectedLesionNumber} onChange={onLesionNumberChange}>
            <option key={-1} value={-1}>------</option>
            {filteredTrackingType === 'selected' &&
              ROIContours.map(roi => (
                <option key={roi.ROINumber} value={roi.ROINumber}>
                  {roi.ROIName}
                </option>
              ))}
          </Select>
        </Grid>
        <Grid item xs={isRecip ? 3 : 3}>
          <Select value={measurementTechnique} onChange={onTechniqueChange}>
            {getMeasurementTechniques().map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </Select>
        </Grid>
      </Grid>
    </Body>
  );
};
FindingSection.propTypes = {
  imageSets: PropTypes.array,
  ROIContours: PropTypes.array,
  criteria: PropTypes.any,
  assessments: PropTypes.array,
  getFilteredROIContours: PropTypes.func,
  getRoiUnionExcludingPsma: PropTypes.func,
  filteredBodyPart: PropTypes.string,
  setFilteredBodyPart: PropTypes.func,
  filteredTrackingType: PropTypes.string,
  setFilteredTrackingType: PropTypes.func,
  filteredPsmaStatus: PropTypes.string,
  setFilteredPsmaStatus: PropTypes.func,
  selectedLesionNumber: PropTypes.number,
  setSelectedLesionNumber: PropTypes.func,
  measurementTechnique: PropTypes.string,
  setMeasurementTechnique: PropTypes.func,
};
